import { DialogRef, TMSScanPalletCreateDo } from "@/types";
import { Alert, Button, Checkbox, Modal } from "antd";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Indicator from "@/components/shared/Indicator";
import { createScanPallet } from "@/apis/tms/order";
import { useGlobalContext } from "@/hooks/context";
interface PalletAddDialogProps {
  className?: string;
  onAddPallet: (
    orderId: string,
    palletId: string,
    openScanDialog: boolean,
  ) => void;
}

const PalletAddDialog = forwardRef<DialogRef, PalletAddDialogProps>(
  (props, ref) => {
    const { className, onAddPallet } = props;
    const { hintPrompt } = useGlobalContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openScanDialog, setOpenScanDialog] = useState(false);
    const [orderNo, setOrderNo] = useState<string | undefined>(undefined);
    const createDoRef = useRef<TMSScanPalletCreateDo | null>(null);
    const callbackRef = useRef<(args: unknown) => void | null>(null);

    const openDialog = useCallback((props: unknown) => {
      const { orderId, groupId, orderNo, callback } = props as {
        orderId: string;
        orderNo: string;
        groupId: number;
        callback: (args: unknown) => void;
      };
      createDoRef.current = {
        groupId,
        orderId,
      };
      callbackRef.current = callback;
      setOrderNo(orderNo);
      setIsModalOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
      createDoRef.current = null;
      setOrderNo(undefined);
      setIsModalOpen(false);
    }, []);

    const createPallet = useCallback(() => {
      if (createDoRef.current == null) return;
      setLoading(true);
      createScanPallet(createDoRef.current)
        .then((pallet) => {
          hintPrompt(
            "success",
            "Create Pallet Success",
            `Pallet ${pallet.no} created successfully`,
          );
          callbackRef.current?.(openScanDialog);
          closeDialog();
          onAddPallet(pallet.orderId, pallet.id, openScanDialog);
        })
        .catch((error) => {
          hintPrompt(
            "error",
            "Create Pallet Failed",
            error.msg ?? "Unknown error",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }, [closeDialog, hintPrompt, callbackRef, openScanDialog, onAddPallet]);

    useImperativeHandle(ref, () => ({
      showModal: openDialog,
      hideModal: closeDialog,
    }));

    return (
      <Modal
        className={className}
        title={`Add Pallet for Order ${orderNo}`}
        open={isModalOpen}
        onCancel={closeDialog}
        footer={null}
        maskClosable={false}
        loading={loading}
      >
        <div>
          <Indicator label="Notice" />
          <Alert
            message="Will create a empty pallet for scanning products into it!"
            type="warning"
            showIcon
          />
        </div>
        <div className="mt-2 ml-2 select-none">
          <Checkbox
            checked={openScanDialog}
            onChange={(e) => setOpenScanDialog(e.target.checked)}
          >
            Open scan dialog after pallet creation
          </Checkbox>
        </div>
        <div className="flex flex-row gap-2 items-center justify-end mt-4">
          <Button
            disabled={loading}
            loading={loading}
            danger
            onClick={closeDialog}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            loading={loading}
            type="primary"
            onClick={createPallet}
          >
            Create Pallet
          </Button>
        </div>
      </Modal>
    );
  },
);

export default PalletAddDialog;
